import type { CollectionReference, DocumentReference } from 'firebase/firestore'
import { FirestoreConnection } from './firestore.connection'
import { adminAccountDocConverter, type AdminAccountDoc } from '../account/admin/admin-account.modl'
import { schoolAccountDocConverter, type SchoolAccountDoc } from '../account/school/school-account.modl'
import { schoolMemoDocConverter, type SchoolMemoDoc } from '../account/school/school-memo.modl'
import { pendingSchoolAccountDocConverter, type PendingSchoolAccountDoc } from '../account/school/pending-school-account.modl'
import { contactDocConverter, type ContactDoc } from '../contact/contact.modl'
import {
  COMPANY_SURVEY_FORM_ID_CURRENT,
  companySurveyFormDocConverter,
  type CompanySurveyFormDoc,
} from '../project/company_survey_form_model'
import {
  companySurveySendListDocConverter,
  type CompanySurveySendListDoc,
} from '../project/company_survey_send_list_model'
import { companySurveyAnswerDocConverter, type CompanySurveyAnswerDoc } from '../project/company_survey_answer_model'
import {
  companySurveyAnswerHistoryDocConverter,
  type CompanySurveyAnswerHistoryDoc,
} from '../project/company_survey_answer_history.modl'
import { projectDocConverter, type ProjectDoc } from '../project/project_model'
import { invoiceItemDocConverter, type InvoiceItemDoc } from '../invoice/invoice-item.modl'
import { addTownCompanyRequestDocConverter, type AddTownCompanyRequestDoc } from '../project/add-town-company-request.modl'
import { projectCounterDocConverter, type ProjectCounterDoc } from '../project/project-counter.modl'
import { townCompanyDocConverter, type TownCompanyDoc } from '../town_company/town-company.modl'
import { systemConfigDocConverter, type SystemConfigDoc } from '../config/system.modl'
import { manualConfigDocConverter, type ManualConfigDoc } from '../config/manual.modl'
import { schoolManualConfigDocConverter, type SchoolManualConfigDoc } from '../config/school-manual.modl'
import type { FormShortUrlDoc } from '../form_short_url/form-short-url.modl'

/**
 * Firestore の参照（collection/doc）生成を1箇所に集約する
 * - パスの直書き・typo・階層変更時の修正漏れを防ぐ
 */
export const refs = {
  // ---- admin accounts ----
  adminAccounts: () =>
    FirestoreConnection.getCollection('admin_accounts').withConverter(adminAccountDocConverter),
  adminAccount: (adminAccountId: string): DocumentReference<AdminAccountDoc, AdminAccountDoc> =>
    FirestoreConnection.getDocumentReference('admin_accounts', adminAccountId).withConverter(adminAccountDocConverter),

  // ---- school accounts ----
  schoolAccounts: () =>
    FirestoreConnection.getCollection('school_accounts').withConverter(schoolAccountDocConverter),
  schoolAccount: (uid: string): DocumentReference<SchoolAccountDoc, SchoolAccountDoc> =>
    FirestoreConnection.getDocumentReference('school_accounts', uid).withConverter(schoolAccountDocConverter),
  /** school_account の履歴サブコレクション（school_accounts/{id}/history） */
  schoolAccountHistories: (uid: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'history'),
  /** school_account のメモサブコレクション（school_accounts/{id}/memos） */
  schoolAccountMemos: (uid: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'memos').withConverter(schoolMemoDocConverter),
  schoolAccountMemo: (uid: string, memoId: string): DocumentReference<SchoolMemoDoc, SchoolMemoDoc> =>
    FirestoreConnection.getDocumentReference('school_accounts', uid, 'memos', memoId).withConverter(
      schoolMemoDocConverter,
    ),

  // ---- pending school accounts ----
  pendingSchoolAccounts: () =>
    FirestoreConnection.getCollection('pending_school_accounts').withConverter(pendingSchoolAccountDocConverter),
  pendingSchoolAccount: (
    pendingSchoolAccountId: string,
  ): DocumentReference<PendingSchoolAccountDoc, PendingSchoolAccountDoc> =>
    FirestoreConnection.getDocumentReference('pending_school_accounts', pendingSchoolAccountId).withConverter(
      pendingSchoolAccountDocConverter,
    ),
  /** pending_school_account の履歴サブコレクション（pending_school_accounts/{id}/history） */
  pendingSchoolAccountHistories: (pendingSchoolAccountId: string) =>
    FirestoreConnection.getCollection('pending_school_accounts', pendingSchoolAccountId, 'history'),

  /**
   * 学校アカウント作成リクエスト（承認フロー用）。
   * ドキュメント作成でトリガーが発火し、Auth 作成・school_accounts 作成・メール送信を行う。
   */
  schoolAccountCreationRequests: () =>
    FirestoreConnection.getCollection('school_account_creation_requests'),
  schoolAccountCreationRequest: (requestId: string) =>
    FirestoreConnection.getDocumentReference('school_account_creation_requests', requestId),

  /**
   * Trigger Email 拡張機能（firestore-send-email）用のメール送信キュー。
   * ここにドキュメントを追加すると拡張機能がメールを送信する。
   * @see https://firebase.google.com/docs/extensions/official/firestore-send-email?hl=ja
   */
  mail: () => FirestoreConnection.getCollection('mail'),

  // ---- contacts (under school_account) ----
  contacts: (uid: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'contacts').withConverter(contactDocConverter),
  contact: (uid: string, contactId: string): DocumentReference<ContactDoc, ContactDoc> =>
    FirestoreConnection.getDocumentReference('school_accounts', uid, 'contacts', contactId).withConverter(
      contactDocConverter,
    ),
  /** contact の履歴サブコレクション（school_accounts/{id}/contacts/{id}/history） */
  contactHistories: (uid: string, contactId: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'contacts', contactId, 'history'),

  // ---- projects (under school_account) ----
  projects: (uid: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'projects').withConverter(projectDocConverter),
  project: (uid: string, projectId: string): DocumentReference<ProjectDoc, ProjectDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
    ).withConverter(projectDocConverter),

  // ---- project counters (under school_account) ----
  projectCounter: (uid: string, year: string): DocumentReference<ProjectCounterDoc, ProjectCounterDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'project_counters',
      year,
    ).withConverter(projectCounterDocConverter),

  // ---- projects (under school_account) ----
  /** 案件番号の日付別シード（全体で1つ、日付ごとに last_index をインクリメント） */
  globalProjectNumberSeed: (dateStr: string) =>
    FirestoreConnection.getDocumentReference('project_number_seeds', dateStr),
  schoolAccountProjects: (uid: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'projects').withConverter(projectDocConverter),
  schoolAccountProject: (
    uid: string,
    projectId: string,
  ): DocumentReference<ProjectDoc, ProjectDoc> =>
    FirestoreConnection.getDocumentReference('school_accounts', uid, 'projects', projectId).withConverter(
      projectDocConverter,
    ),

  // ---- invoice items (under school_account project) ----
  schoolAccountInvoiceItems: (uid: string, projectId: string) =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'invoice_items',
    ).withConverter(invoiceItemDocConverter),
  schoolAccountInvoiceItem: (
    uid: string,
    projectId: string,
    invoiceItemId: string,
  ): DocumentReference<InvoiceItemDoc, InvoiceItemDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'invoice_items',
      invoiceItemId,
    ).withConverter(invoiceItemDocConverter),

  // ---- town companies (under school_account project) ----
  schoolAccountTownCompanies: (uid: string, projectId: string) =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
    ).withConverter(townCompanyDocConverter),
  schoolAccountTownCompany: (
    uid: string,
    projectId: string,
    townCompanyDocId: string,
  ): DocumentReference<TownCompanyDoc, TownCompanyDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
      townCompanyDocId,
    ).withConverter(townCompanyDocConverter),

  /** town_company の履歴サブコレクション（.../town_companies/{docId}/town_company_history） */
  townCompanyHistories: (
    uid: string,
    projectId: string,
    townCompanyDocId: string,
  ) =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
      townCompanyDocId,
      'town_company_history',
    ),

  /** project の履歴サブコレクション（school_accounts/{id}/projects/{id}/history） */
  projectHistories: (uid: string, projectId: string) =>
    FirestoreConnection.getCollection('school_accounts', uid, 'projects', projectId, 'history'),

  // ---- add town company requests (under school_account project) ----
  addTownCompanyRequests: (uid: string, projectId: string) =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'requests',
    ).withConverter(addTownCompanyRequestDocConverter),
  addTownCompanyRequest: (
    uid: string,
    projectId: string,
    requestId: string,
  ): DocumentReference<AddTownCompanyRequestDoc, AddTownCompanyRequestDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'requests',
      requestId,
    ).withConverter(addTownCompanyRequestDocConverter),

  // ---- town_companies ----
  townCompanies: (uid: string, projectId: string) =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
    ).withConverter(townCompanyDocConverter),
  townCompany: (
    uid: string,
    projectId: string,
    townCompanyDocId: string,
  ): DocumentReference<TownCompanyDoc, TownCompanyDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
      townCompanyDocId,
    ).withConverter(townCompanyDocConverter),

  // ---- company survey form（1プロジェクト1ドキュメント、id=current） ----
  /** company_survey_form コレクション（1プロジェクト1フォームのため doc id は current） */
  companySurveyForms: (
    uid: string,
    projectId: string,
  ): CollectionReference<CompanySurveyFormDoc> =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'company_survey_form',
    ).withConverter(companySurveyFormDocConverter),

  /** company_survey_form ドキュメント参照（1プロジェクト1つ、id=current 固定） */
  companySurveyForm: (
    uid: string,
    projectId: string,
  ): DocumentReference<CompanySurveyFormDoc, CompanySurveyFormDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'company_survey_form',
      COMPANY_SURVEY_FORM_ID_CURRENT,
    ).withConverter(companySurveyFormDocConverter),

  // ---- company survey send list（1プロジェクトで複数、郵送確定ごとに1ドキュメント） ----
  companySurveySendLists: (
    uid: string,
    projectId: string,
  ): CollectionReference<CompanySurveySendListDoc> =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'company_survey_send_lists',
    ).withConverter(companySurveySendListDocConverter),

  companySurveySendList: (
    uid: string,
    projectId: string,
    sendListId: string,
  ): DocumentReference<CompanySurveySendListDoc, CompanySurveySendListDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'company_survey_send_lists',
      sendListId,
    ).withConverter(companySurveySendListDocConverter),

  // ---- company survey answer（1事業所1ドキュメント、current 固定） ----
  companySurveyAnswer: (
    uid: string,
    projectId: string,
    townCompanyDocId: string,
  ): DocumentReference<CompanySurveyAnswerDoc, CompanySurveyAnswerDoc> =>
    FirestoreConnection.getDocumentReference(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
      townCompanyDocId,
      'survey_answers',
      'current',
    ).withConverter(companySurveyAnswerDocConverter),

  /** survey_answer の履歴サブコレクション（.../survey_answers/current/history） */
  companySurveyAnswerHistories: (uid: string, projectId: string, townCompanyDocId: string) =>
    FirestoreConnection.getCollection(
      'school_accounts',
      uid,
      'projects',
      projectId,
      'town_companies',
      townCompanyDocId,
      'survey_answers',
      'current',
      'history',
    ).withConverter(companySurveyAnswerHistoryDocConverter),

  // ---- form short URLs（事業所フォーム短縮URL、school_accounts 等と並列） ----
  formShortUrls: () =>
    FirestoreConnection.getCollection('form_short_urls') as CollectionReference<FormShortUrlDoc>,
  formShortUrl: (shortCode: string) =>
    FirestoreConnection.getDocumentReference('form_short_urls', shortCode) as DocumentReference<FormShortUrlDoc>,

  /** moneyforward_oauth/current ... Money Forward OAuth token store (admin only) */
  moneyforwardOAuth: () => FirestoreConnection.getCollection('moneyforward_oauth'),
  moneyforwardOAuthCurrent: () => FirestoreConnection.getDocumentReference('moneyforward_oauth', 'current'),

  // ---- config (システム設定) ----
  /** config/system ドキュメント（メンテナンス状態・利用規約URL） */
  systemConfig: (): DocumentReference<SystemConfigDoc, SystemConfigDoc> =>
    FirestoreConnection.getDocumentReference('config', 'system').withConverter(systemConfigDocConverter),
  /** config/manual ドキュメント（マニュアル表示内容） */
  manualConfig: (): DocumentReference<ManualConfigDoc, ManualConfigDoc> =>
    FirestoreConnection.getDocumentReference('config', 'manual').withConverter(manualConfigDocConverter),
  /** config/school_manual ドキュメント（学校向けFAQ表示内容） */
  schoolManualConfig: (): DocumentReference<SchoolManualConfigDoc, SchoolManualConfigDoc> =>
    FirestoreConnection.getDocumentReference('config', 'school_manual').withConverter(
      schoolManualConfigDocConverter,
    ),
} as const

// 型の export（必要ならサービス側で使えるように）
export type {
  AdminAccountDoc,
  SchoolAccountDoc,
  SchoolMemoDoc,
  PendingSchoolAccountDoc,
  ContactDoc,
  ProjectDoc,
  AddTownCompanyRequestDoc,
  TownCompanyDoc,
  CompanySurveyFormDoc,
  CompanySurveySendListDoc,
  CompanySurveyAnswerDoc,
  CompanySurveyAnswerHistoryDoc,
  ProjectCounterDoc,
  SystemConfigDoc,
  ManualConfigDoc,
  SchoolManualConfigDoc,
}

